import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Brain, Eye, Fingerprint, MessageCircle, Sparkles, ChevronRight, ArrowRight } from 'lucide-react';

const capabilities = [
  {
    id: 'memoriser',
    icon: Brain,
    label: 'Mémoriser',
    headline: 'Retient chaque échange, chaque détail.',
    desc: 'Emails, appels, messages, réunions. Le clone absorbe tout et n\'oublie jamais rien.',
    example: {
      input: 'Quand est-ce qu\'on a parlé à Durand pour la dernière fois ?',
      output: 'Jeudi 14h32. Justine l\'a appelé pour le devis. Il a demandé 10% de remise. Elle a dit qu\'elle revenait vers lui vendredi.',
    },
    color: '#3b82f6',
    glow: 'rgba(59, 130, 246, 0.4)',
  },
  {
    id: 'comprendre',
    icon: Eye,
    label: 'Comprendre',
    headline: 'Détecte ce qui compte vraiment.',
    desc: 'Signaux faibles, tensions, opportunités. Le clone lit entre les lignes de vos échanges.',
    example: {
      input: 'Des problèmes en ce moment ?',
      output: 'Le client Morel montre des signes d\'insatisfaction depuis 3 échanges. Ton plus sec, délais de réponse plus longs. Je recommande une action rapide.',
    },
    color: '#8b5cf6',
    glow: 'rgba(139, 92, 246, 0.4)',
  },
  {
    id: 'incarner',
    icon: Fingerprint,
    label: 'Incarner',
    headline: 'Agit comme vos collaborateurs.',
    desc: 'Le clone analyse le ton, les réflexes, la logique de décision de chaque personne. Il reproduit leur style et prolonge leur manière de travailler.',
    example: {
      input: 'Rédige une relance pour Morel, comme Justine.',
      output: 'Bonjour M. Morel, je reviens vers vous suite à notre échange. Je comprends vos interrogations sur le délai — je m\'en occupe personnellement. Bien à vous, Justine',
    },
    color: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.5)',
    featured: true,
  },
  {
    id: 'interagir',
    icon: MessageCircle,
    label: 'Interagir',
    headline: 'Posez la question, il a la réponse.',
    desc: 'En langage naturel. À l\'écrit ou à l\'oral. Comme une conversation avec quelqu\'un qui sait tout.',
    example: {
      input: 'Explique-moi le dossier Martin comme si j\'étais le client.',
      output: 'Votre commande a été validée le 12 mars. Livraison prévue le 28. Un retard de 3 jours a été signalé, votre interlocuteur vous a prévenu mardi par mail.',
    },
    color: '#22c55e',
    glow: 'rgba(34, 197, 94, 0.4)',
  },
  {
    id: 'proposer',
    icon: Sparkles,
    label: 'Proposer',
    headline: 'Vous dit quoi faire, maintenant.',
    desc: 'Le clone ne se contente pas de répondre. Il recommande la bonne action au bon moment.',
    example: {
      input: 'Quoi faire avec le client Morel ?',
      output: 'Relance personnalisée recommandée. J\'ai préparé un message dans le style de Justine avec un point sur les délais et un geste commercial.',
    },
    color: '#ec4899',
    glow: 'rgba(236, 72, 153, 0.4)',
  },
];

const TypingReveal = ({ text, speed = 15, active }) => {
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    if (!active) { setDisplayed(''); indexRef.current = 0; return; }
    indexRef.current = 0;
    setDisplayed('');
    const interval = setInterval(() => {
      indexRef.current++;
      setDisplayed(text.slice(0, indexRef.current));
      if (indexRef.current >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, active]);

  return (
    <span>
      {displayed}
      {active && displayed.length < text.length && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="inline-block w-[2px] h-[1em] bg-current ml-0.5 align-middle"
        />
      )}
    </span>
  );
};

const CapabilityCard = ({ cap, index, isActive, onClick }) => {
  const Icon = cap.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={onClick}
      className={`relative group cursor-pointer rounded-2xl transition-all duration-500 overflow-hidden ${
        cap.featured && isActive ? 'md:col-span-2' : ''
      }`}
      data-testid={`capability-${cap.id}`}
    >
      {/* Background glow on active */}
      <motion.div
        className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500"
        style={{
          background: `radial-gradient(ellipse at center, ${cap.glow}, transparent 70%)`,
          opacity: isActive ? 0.15 : 0,
        }}
      />

      {/* Border */}
      <div className={`relative h-full p-6 md:p-8 rounded-2xl border backdrop-blur-sm transition-all duration-500 ${
        isActive
          ? 'border-white/20 bg-[#0f0f17]/90'
          : 'border-white/5 bg-[#0f0f17]/60 hover:border-white/10'
      }`}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <motion.div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: `${cap.color}15`,
              border: `1px solid ${cap.color}30`,
            }}
            animate={isActive ? {
              boxShadow: [
                `0 0 0px ${cap.color}00`,
                `0 0 20px ${cap.glow}`,
                `0 0 0px ${cap.color}00`,
              ],
            } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Icon className="w-5 h-5" style={{ color: cap.color }} />
          </motion.div>
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: cap.color }}>{cap.label}</p>
          </div>
          {cap.featured && (
            <motion.span
              className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: `${cap.color}15`, color: cap.color, border: `1px solid ${cap.color}30` }}
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Unique
            </motion.span>
          )}
        </div>

        {/* Headline */}
        <h3 className="text-lg md:text-xl font-semibold text-[#f5f5f5] mb-2 tracking-tight">
          {cap.headline}
        </h3>
        <p className="text-[#71717a] text-sm leading-relaxed mb-4">
          {cap.desc}
        </p>

        {/* Demo zone */}
        <AnimatePresence mode="wait">
          {isActive && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="pt-4 border-t border-white/5 space-y-3">
                {/* Question */}
                <div className="flex gap-2 items-start">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#52525b] mt-1 shrink-0">Q</span>
                  <p className="text-[#a1a1aa] text-sm italic">{cap.example.input}</p>
                </div>
                {/* Answer */}
                <div className="flex gap-2 items-start">
                  <span className="text-[10px] uppercase tracking-wider font-bold shrink-0 mt-1" style={{ color: cap.color }}>R</span>
                  <p className="text-[#f5f5f5] text-sm">
                    <TypingReveal text={cap.example.output} active={isActive} speed={12} />
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* "See more" hint */}
        {!isActive && (
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-[#52525b] group-hover:text-[#71717a] transition-colors">Voir l'exemple</span>
            <ChevronRight className="w-3 h-3 text-[#52525b] group-hover:text-[#71717a] group-hover:translate-x-0.5 transition-all" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const CloneCapabilities = () => {
  const [activeId, setActiveId] = useState('incarner');
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Auto-cycle when not interacted
  const [autoCycle, setAutoCycle] = useState(true);
  const cycleRef = useRef(null);

  useEffect(() => {
    if (!autoCycle || !isInView) return;
    cycleRef.current = setInterval(() => {
      setActiveId(prev => {
        const idx = capabilities.findIndex(c => c.id === prev);
        return capabilities[(idx + 1) % capabilities.length].id;
      });
    }, 5000);
    return () => clearInterval(cycleRef.current);
  }, [autoCycle, isInView]);

  const handleClick = (id) => {
    setAutoCycle(false);
    clearInterval(cycleRef.current);
    setActiveId(id);
  };

  return (
    <section
      ref={sectionRef}
      id="capabilities"
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
      data-testid="capabilities-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]"
          style={{ background: 'radial-gradient(circle, #3b82f6, transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-[#f59e0b] font-bold mb-4">Les 5 capacités du clone</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-4">
            Bien plus qu'une mémoire.
          </h2>
          <p className="text-[#a1a1aa] max-w-2xl mx-auto text-base md:text-lg">
            Le clone ne se contente pas de stocker. Il comprend, il analyse, il prolonge la manière
            de travailler de chaque collaborateur.
          </p>
        </motion.div>

        {/* Progress indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex justify-center gap-2 mb-12"
        >
          {capabilities.map((cap) => (
            <button
              key={cap.id}
              onClick={() => handleClick(cap.id)}
              className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300"
              style={{
                background: activeId === cap.id ? `${cap.color}15` : 'transparent',
                border: `1px solid ${activeId === cap.id ? `${cap.color}40` : 'rgba(255,255,255,0.05)'}`,
              }}
              data-testid={`cap-nav-${cap.id}`}
            >
              <div
                className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  background: activeId === cap.id ? cap.color : '#52525b',
                  boxShadow: activeId === cap.id ? `0 0 8px ${cap.glow}` : 'none',
                }}
              />
              <span
                className="text-xs font-medium transition-colors duration-300 hidden sm:inline"
                style={{ color: activeId === cap.id ? cap.color : '#52525b' }}
              >
                {cap.label}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Cards grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          layout
        >
          {capabilities.map((cap, index) => (
            <CapabilityCard
              key={cap.id}
              cap={cap}
              index={index}
              isActive={activeId === cap.id}
              onClick={() => handleClick(cap.id)}
            />
          ))}
        </motion.div>

        {/* Bottom quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-16 text-center"
        >
          <p className="text-xl md:text-2xl font-semibold text-[#f5f5f5]">
            "Le clone ne remplace pas le collaborateur.
          </p>
          <p className="text-xl md:text-2xl font-semibold mt-1">
            <motion.span
              className="text-[#f59e0b]"
              animate={{
                textShadow: [
                  '0 0 10px rgba(245, 158, 11, 0.2)',
                  '0 0 25px rgba(245, 158, 11, 0.5)',
                  '0 0 10px rgba(245, 158, 11, 0.2)',
                ],
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              Il prolonge sa manière de travailler."
            </motion.span>
          </p>
        </motion.div>

        {/* CTA vers la page clone collaborateur */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <Link
            to="/clone-collaborateur-entreprise"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 bg-[#0f0f17]/80 text-[#a1a1aa] hover:text-[#f5f5f5] hover:border-[#3b82f6]/50 transition-all group"
            data-testid="capabilities-cta-clone"
          >
            {"D\u00e9couvrir le clone collaborateur"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CloneCapabilities;
